'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { apiFetch } from '@/lib/api-fetch'
import { AppIcon } from '@/components/ui/icons'
import { useShortDramaAccess } from '@/lib/short-drama/access-client'

// `quick-video` remains the persisted tab id, but the tab now represents merged AI对话作品.
// Keep `image-text` in the type union for legacy callers, then route it into the merged tab.
export const WORKSPACE_PROJECT_KINDS = ['short-drama', 'quick-video', 'image-text'] as const

export type WorkspaceProjectKind = typeof WORKSPACE_PROJECT_KINDS[number]

type ProjectKindCounts = Partial<Record<WorkspaceProjectKind, number | null>>
type ProjectKindTabIcon = 'sparkles' | 'messageCircle'

interface WorkspaceProjectKindTabsProps {
  activeKind: WorkspaceProjectKind
  counts?: ProjectKindCounts
  className?: string
  onChange?: (kind: WorkspaceProjectKind) => void
  hrefForKind?: (kind: WorkspaceProjectKind) => string
}

const DEFAULT_COUNTS: ProjectKindCounts = {}
const PROJECT_KIND_COUNTS_CACHE_TTL_MS = 60_000
let projectKindCountsCache: { value: ProjectKindCounts; expiresAt: number } | null = null
let projectKindCountsPromise: Promise<ProjectKindCounts> | null = null

function workspaceProjectKindHref(kind: WorkspaceProjectKind) {
  return `/workspace?type=${kind === 'image-text' ? 'quick-video' : kind}`
}

function readCachedProjectKindCounts() {
  const now = Date.now()
  return projectKindCountsCache && projectKindCountsCache.expiresAt > now
    ? projectKindCountsCache.value
    : null
}

async function fetchProjectKindCounts(): Promise<ProjectKindCounts> {
  const cachedCounts = readCachedProjectKindCounts()
  if (cachedCounts) return cachedCounts
  if (projectKindCountsPromise) return projectKindCountsPromise

  projectKindCountsPromise = (async () => {
    const accessResponse = await apiFetch('/api/short-drama/access')
    const shortDramaEnabled = accessResponse.ok
      ? ((await accessResponse.json()) as { enabled?: boolean }).enabled !== false
      : true
    const [shortDramaResponse, aiChatWorksResponse] = await Promise.all([
      shortDramaEnabled
        ? apiFetch('/api/projects?page=1&pageSize=1&type=short-drama&countOnly=1')
        : Promise.resolve(new Response(JSON.stringify({ pagination: { total: 0 } }), { status: 200 })),
      apiFetch('/api/workspace/ai-chat-projects?page=1&pageSize=1&preview=0&countOnly=1'),
    ])

    if (!shortDramaResponse.ok || !aiChatWorksResponse.ok) {
      throw new Error('Failed to fetch workspace project counts')
    }

    const [shortDrama, aiChatWorks] = await Promise.all([
      shortDramaResponse.json() as Promise<{ pagination?: { total?: number } }>,
      aiChatWorksResponse.json() as Promise<{ pagination?: { total?: number } }>,
    ])

    const value = {
      'short-drama': shortDrama.pagination?.total ?? null,
      'quick-video': aiChatWorks.pagination?.total ?? null,
      'image-text': null,
    }
    projectKindCountsCache = {
      value,
      expiresAt: Date.now() + PROJECT_KIND_COUNTS_CACHE_TTL_MS,
    }
    return value
  })().finally(() => {
    projectKindCountsPromise = null
  })

  return projectKindCountsPromise
}

export function useWorkspaceProjectKindCounts(enabled = true) {
  const [counts, setCounts] = useState<ProjectKindCounts>(() => (
    enabled ? readCachedProjectKindCounts() || DEFAULT_COUNTS : DEFAULT_COUNTS
  ))

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    fetchProjectKindCounts()
      .then((nextCounts) => {
        if (!cancelled) setCounts(nextCounts)
      })
      .catch(() => {
        if (!cancelled) setCounts(DEFAULT_COUNTS)
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  return counts
}

export function getWorkspaceProjectKindHref(kind: WorkspaceProjectKind) {
  return workspaceProjectKindHref(kind)
}

export default function WorkspaceProjectKindTabs({
  activeKind,
  counts = DEFAULT_COUNTS,
  className = '',
  onChange,
  hrefForKind = workspaceProjectKindHref,
}: WorkspaceProjectKindTabsProps) {
  const t = useTranslations('workspace')
  const { enabled: shortDramaAccessEnabled } = useShortDramaAccess(true)
  const tabs: Array<{ id: WorkspaceProjectKind; label: string; icon: ProjectKindTabIcon }> = [
    ...(shortDramaAccessEnabled ? [{ id: 'short-drama' as const, label: t('projectTabs.shortDrama'), icon: 'sparkles' as const }] : []),
    { id: 'quick-video', label: t('projectTabs.aiChatWorks'), icon: 'messageCircle' },
  ]

  return (
    <div className={`flex w-full overflow-x-auto rounded-[0.55rem] border border-white/55 bg-white/45 p-1 shadow-[0_16px_42px_rgba(32,46,88,0.08)] backdrop-blur-[14px] sm:w-fit ${className}`}>
      {tabs.map((tab) => {
        const active = tab.id === activeKind
        const count = counts[tab.id]
        const tabClassName = `inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[0.55rem] px-3 py-2 text-[13px] font-semibold transition sm:min-w-fit sm:flex-none sm:justify-start sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm ${active
          ? 'bg-[linear-gradient(135deg,#3d5cff_0%,#6c74ff_42%,#8d5eff_100%)] text-white shadow-[0_12px_28px_rgba(69,84,189,0.22)]'
          : 'text-[var(--glass-text-secondary)] hover:bg-white/60 hover:text-[var(--glass-text-primary)]'
        }`
        const content = (
          <>
            <AppIcon name={tab.icon} className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
            <span className="min-w-0 truncate">{tab.label}</span>
            {typeof count === 'number' ? (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${active ? 'bg-white/22 text-white' : 'bg-[var(--glass-bg-muted)] text-[var(--glass-text-tertiary)]'}`}>
                {count}
              </span>
            ) : null}
          </>
        )

        if (onChange) {
          return (
            <button
              key={tab.id}
              type="button"
              data-project-kind-tab={tab.id}
              onClick={() => onChange(tab.id)}
              aria-current={active ? 'page' : undefined}
              className={tabClassName}
            >
              {content}
            </button>
          )
        }

        return (
          <Link
            key={tab.id}
            href={hrefForKind(tab.id)}
            prefetch={false}
            data-project-kind-tab={tab.id}
            aria-current={active ? 'page' : undefined}
            className={tabClassName}
          >
            {content}
          </Link>
        )
      })}
    </div>
  )
}
